import { useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LabelList, CartesianGrid, ComposedChart, Line, Legend } from "recharts";
import { Filter, Info, TrendingUp } from "lucide-react";
import { Section } from "@/mockups/dc";

export const C = { teal: "#1F8A7C", fill: "#5CC4B4", soft: "#CFEAE5", neg: "#C2483A", warn: "#B7791F", grey: "#9AB0B8", ink: "#13212A", grid: "#E6EEEF" };
const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : "0%");

const Hint = () => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button className="btn btn-sm" onClick={() => setOpen(!open)} aria-label="How to use this chart" style={{ borderRadius: 999, width: 28, height: 28, padding: 0, justifyContent: "center" }} data-testid="lx-funnel-info"><Info size={14} /></button>
      {open && <div className="card" style={{ position: "absolute", right: 0, top: 34, width: 260, padding: 12, fontSize: 12, zIndex: 5 }} data-testid="lx-funnel-hint">Click any bar to see the leads behind it in a list below. Click a name to open that lead in a new tab.</div>}
    </div>
  );
};

const steps = (f) => [
  { k: "in", label: "Leads in", v: f.in, base: 0, color: C.ink, of: f.in },
  { k: "not_a_lead", label: "Not a lead", v: f.not_a_lead, base: f.in - f.not_a_lead, color: C.grey, of: f.in },
  { k: "actionable", label: "Actionable", v: f.actionable, base: 0, color: C.teal, of: f.actionable },
  { k: "contacted", label: "Contacted", v: f.contacted, base: 0, color: C.teal, of: f.actionable },
  { k: "qualified", label: "Qualified", v: f.qualified, base: 0, color: C.teal, of: f.actionable },
  { k: "won", label: "Won", v: f.won, base: 0, color: C.fill, of: f.actionable },
  { k: "lost", label: "Lost", v: f.lost, base: f.won, color: C.neg, of: f.actionable },
  { k: "no_response", label: "No response", v: f.no_response, base: f.won + f.lost, color: C.warn, of: f.actionable },
  { k: "open", label: "Still open", v: f.open, base: f.won + f.lost + f.no_response, color: C.soft, of: f.actionable },
].map((s) => ({ ...s, text: `${s.v} · ${pct(s.v, s.of)}` }));

export const ExecFunnel = ({ f, onPick }) => {
  const data = steps(f);
  return (
    <Section title="Lead funnel" sub="How leads move from first contact to first booking. % of actionable leads (leads in minus not-a-lead), same as the conversion rate on the old view." icon={Filter} testid="lx-funnel" right={<Hint />}>
      <div style={{ height: 300 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 24, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={C.grid} />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={56} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip formatter={(v, n) => (n === "base" ? null : v)} cursor={{ fill: "rgba(19,33,42,.04)" }} />
            <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
            <Bar dataKey="v" name="Leads" stackId="w" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(d) => onPick({ stage: d.k, title: d.label, n: d.v })}>
              {data.map((s) => <Cell key={s.k} fill={s.color} data-testid={`lx-bar-${s.k}`} />)}
              <LabelList dataKey="text" position="top" style={{ fontSize: 10, fontFamily: "JetBrains Mono, monospace", fill: C.ink }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }} data-testid="lx-funnel-summary">
        <b className="mono" style={{ color: C.ink }}>{f.in}</b> leads in → <b className="mono" style={{ color: C.ink }}>{f.contacted}</b> actioned ({pct(f.contacted, f.actionable)}) → <b className="mono" style={{ color: C.teal }}>{f.won}</b> won ({pct(f.won, f.actionable)}) · <b className="mono" style={{ color: C.neg }}>{f.lost}</b> lost · <b className="mono">{f.no_response}</b> no response · <b className="mono">{f.open}</b> still open
      </div>
    </Section>
  );
};

const GRAIN = { day: "by day", week: "by week", month: "by month" };
const LBL = { fontSize: 9, fontFamily: "JetBrains Mono, monospace", fill: C.ink };
const k$ = (v) => (v ? `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}` : "");
const stack = (trend) => trend.map((r) => ({ ...r, actioned_only: Math.max(r.actioned - r.won, 0), waiting: Math.max(r.in - r.actioned, 0) }));
const InFlow = ({ n, prev }) => {
  const d = prev ? Math.round(((n - prev) / prev) * 100) : null;
  const down = d !== null && d < 0;
  return (
    <div className="mono" style={{ fontSize: 12, textAlign: "right" }} data-testid="lx-trend-inflow">
      <div className="label">Leads coming in</div>
      <b style={{ fontSize: 18 }}>{n}</b> <span style={{ color: d === null ? "var(--muted)" : down ? C.neg : C.teal, fontWeight: 700 }}>{d === null ? "no prev data" : `${d > 0 ? "+" : ""}${d}% vs prev`}</span>
      <div className="muted" style={{ fontSize: 11 }}>prev period {prev}</div>
    </div>
  );
};
const TrendTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  return (
    <div className="card" style={{ padding: 10, fontSize: 12 }} data-testid="lx-trend-tip">
      <b>{label}</b>
      <div className="mono">Leads in {r.in} · actioned {r.actioned} · won {r.won}</div>
      {r.revenue > 0 && <div className="mono" style={{ color: C.teal, fontWeight: 700 }}>Revenue ${Number(r.revenue).toLocaleString()}</div>}
    </div>
  );
};

export const ExecTrend = ({ trend, grain, label, leadsIn, prevLeadsIn }) => (
  <Section title="Leads, actions & revenue" sub={`${label} · ${GRAIN[grain]}. Each bar = leads coming in (total on top), stacked as won, actioned and not actioned yet. Line = revenue from won leads (right axis, $).`} icon={TrendingUp} testid="lx-trend" right={<InFlow n={leadsIn} prev={prevLeadsIn} />}>
    <div style={{ height: 280 }}>
      <ResponsiveContainer>
        <ComposedChart data={stack(trend)} margin={{ top: 24, right: 8, left: -8, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={C.grid} />
          <XAxis dataKey="m" tick={{ fontSize: 11 }} minTickGap={8} />
          <YAxis yAxisId="n" tick={{ fontSize: 11 }} allowDecimals={false} />
          <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => k$(v) || "$0"} />
          <Tooltip content={<TrendTip />} cursor={{ fill: "rgba(19,33,42,.04)" }} />
          <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span style={{ color: C.ink }}>{v}</span>} />
          <Bar yAxisId="n" dataKey="won" name="Won" stackId="s" fill={C.teal}>
            <LabelList dataKey="won" position="center" formatter={(v) => v || ""} style={{ ...LBL, fill: "#fff", fontWeight: 700 }} />
          </Bar>
          <Bar yAxisId="n" dataKey="actioned_only" name="Actioned" stackId="s" fill={C.fill}>
            <LabelList dataKey="actioned_only" position="center" formatter={(v) => v || ""} style={{ ...LBL, fill: "#fff", fontWeight: 700 }} />
          </Bar>
          <Bar yAxisId="n" dataKey="waiting" name="Not actioned yet" stackId="s" fill="#C9D6DA" radius={[3, 3, 0, 0]}>
            <LabelList dataKey="waiting" position="center" formatter={(v) => v || ""} style={{ ...LBL, fill: C.ink, fontWeight: 700 }} />
            <LabelList dataKey="in" position="top" formatter={(v) => v || ""} style={{ ...LBL, fontSize: 10, fill: C.ink, fontWeight: 800 }} />
          </Bar>
          <Line yAxisId="r" dataKey="revenue" name="Revenue ($)" stroke="#5B6B73" strokeOpacity={0.7} strokeWidth={1.5} dot={{ r: 2, fill: "#5B6B73" }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </Section>
);
